import { Component, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions, MatDialogTitle, MatDialogContent, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { AppSelectors } from '../../../app.state';
import { SubjectJsonModel } from '../../../subject/subject.json.model';
import { ConsumerJsonModel } from '../../../consumer/consumer.json.model';
import { Store } from '@ngxs/store';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { getMinFilterLengthText, MIN_NAME_LENGTH } from '../../util';

export interface CopyDialogData {
  consumerId: string
  name?: string
  competencyId?: string
  treeId?: string
}

interface Consumer {
  id: string
  name: string
}

interface Competency {
  id: string
  name: string
  treeId: string
}

@Component({
  selector: 'pixl-copy-dialog',
  imports: [
    MatButtonModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatCardModule,
    MatIconModule,
    FormsModule,
    CommonModule,
  ],
  templateUrl: './copy-dialog.component.html',
  styleUrl: './copy-dialog.component.scss'
})
export class CopyDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<CopyDialogComponent>);
  private readonly store = inject(Store);
  private appState$ = this.store.select(AppSelectors.appState);
  private fb = inject(FormBuilder);

  public readonly data: CopyDialogData = inject(MAT_DIALOG_DATA);
  public consumers: Consumer[] = []; // List of all available consumers
  public competencies: Competency[] = []; // List of all available competencies
  public formGroup: FormGroup;
  public showName = signal(false);
  public showHint = signal(false);
  public hintText = signal('');

  constructor() {
    this.formGroup = this.fb.group({
      consumerId: new FormControl(this.data.consumerId)
    });

    if (this.data.name) {
      this.formGroup.addControl('name', new FormControl(this.data.name, [Validators.required, Validators.minLength(MIN_NAME_LENGTH)]));
      this.showName.set(true);
    }

    if (this.data.competencyId && this.data.treeId) {
      this.formGroup.addControl('competencyId', new FormControl(this.data.competencyId));
      this.formGroup.addControl('treeId', new FormControl(this.data.treeId));
    }
  }

  public ngOnInit(): void {
    this.appState$.subscribe(({ isLoaded, consumers, subjects }) => {
      if (isLoaded) {
        consumers.forEach((entry: ConsumerJsonModel) => {
          this.consumers.push({
            id: entry.id,
            name: entry.name
          });
        });
        this.consumers = [...this.consumers.sort((a, b) => {
          return a.name.localeCompare(b.name);
        })];

        subjects.forEach((entry: SubjectJsonModel) => {
          this.competencies.push({
            id: entry.id,
            name: entry.name,
            treeId: entry.treeId
          });
        });
        this.competencies = [...this.competencies.sort((a, b) => {
          return a.name.localeCompare(b.name);
        })];
      }
    });

    this.formGroup.markAllAsTouched();
  }

  public onKeyUpEvent(ev: Event) {
    const val = (ev.target as HTMLInputElement).value;
    this.data.name = val;

    if (val.length < MIN_NAME_LENGTH) {
      this.showHint.set(true);
      if (val.length == 0) {
        this.hintText.set('Eingabe darf nicht leer sein');
      }
      else {
        this.hintText.set(getMinFilterLengthText(MIN_NAME_LENGTH));
      }
      return;
    }

    this.showHint.set(false);
  }

  public changeConsumer(consumerId: string) {
    this.data.consumerId = consumerId;
  }

  public changeCompetency(competencyId: string) {
    this.data.competencyId = competencyId;
    if (this.competencies) {
      const competency = this.competencies.find((entry) => {
        return entry.id === this.data.competencyId;
      });
      if (competency) {
        this.data.treeId = competency.treeId;
      }
    }
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onSave(): void {
    if (this.formGroup.valid) {
      this.dialogRef.close(this.data);
    }
  }
}
